import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { MaterialCategory } from '@app/models';

export type MaterialSortKey = 'default' | 'newest' | 'alphabetical' | 'popular';

export interface MaterialSortOption {
  key: MaterialSortKey;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'materials-search-header',
  templateUrl: './materials-search-header.component.html',
  styleUrl: './materials-search-header.component.scss',
  standalone: false
})
export class MaterialsSearchHeaderComponent implements OnChanges {
  @Input() title = 'EDU Healthcare Resource Library';
  @Input() subtitle = 'Browse therapy resources, visual supports, and educational materials';

  @Input() materialCategories: MaterialCategory[] = [];
  @Input() visibleCategoryCap = 4;

  @Input() searchText = '';
  @Input() selectedCategory: string | null = '';
  @Input() sort: MaterialSortKey = 'default';
  @Input() filtersBadgeCount = 0;

  @Output() searchTextChange = new EventEmitter<string>();
  @Output() searchSubmit = new EventEmitter<void>();
  @Output() searchClear = new EventEmitter<void>();
  @Output() categoryChange = new EventEmitter<string>();
  @Output() sortChange = new EventEmitter<MaterialSortKey>();
  @Output() filtersClick = new EventEmitter<void>();

  sortOptions: MaterialSortOption[] = [
    { key: 'default', label: 'Default' },
    { key: 'newest', label: 'Newest' },
    { key: 'alphabetical', label: 'A-Z' },
    { key: 'popular', label: 'Popular (Coming soon)', disabled: true }
  ];

  showOverflow = false;

  // Recomputed in ngOnChanges from inputs so the *ngFor over chips and the
  // sort-label binding don't allocate fresh arrays / scan the options array
  // on every change-detection cycle.
  visibleCategories: MaterialCategory[] = [];
  overflowCategories: MaterialCategory[] = [];
  currentSortLabel = 'Default';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['materialCategories'] || changes['visibleCategoryCap']) {
      const cats = this.materialCategories ?? [];
      this.visibleCategories = cats.length ? cats.slice(0, this.visibleCategoryCap) : [];
      this.overflowCategories = cats.length ? cats.slice(this.visibleCategoryCap) : [];
    }
    if (changes['sort']) {
      this.currentSortLabel = this.sortOptions.find(o => o.key === this.sort)?.label ?? 'Default';
    }
  }

  isCategoryActive(name: string | null | undefined): boolean {
    const current = this.selectedCategory ?? '';
    return (name ?? '') === current;
  }

  onSearchInput(value: string) {
    this.searchText = value;
    this.searchTextChange.emit(value);
  }

  onSearchEnter() {
    this.searchSubmit.emit();
  }

  onSearchClear() {
    this.searchText = '';
    this.searchTextChange.emit('');
    this.searchClear.emit();
  }

  onChipClick(name: string | null) {
    const next = name ?? '';
    if (next === (this.selectedCategory ?? '')) return;
    this.selectedCategory = next;
    this.categoryChange.emit(next);
    this.showOverflow = false;
  }

  onSortPick(option: MaterialSortOption) {
    if (option.disabled) return;
    if (option.key === this.sort) return;
    this.sort = option.key;
    this.sortChange.emit(option.key);
  }

  onFiltersClick() {
    this.filtersClick.emit();
  }

  toggleOverflow() {
    this.showOverflow = !this.showOverflow;
  }
}
